import { Flex, Tooltip, type TooltipProps } from '@chakra-ui/react'
import type { Key, ReactElement, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { BaseText } from '../BaseText'
import { DatePicker, type DatePickerProps } from '../DatePicker/DatePicker'
import { NumberBox } from '../NumberBox'
import { TabSwitcher, type TabSwitcherProps } from '../TabSwitcher'
import { BaseTooltip } from '../Tooltip'
import { BaseButton, type BaseButtonProps } from '../buttons/BaseButton'
import { CreateButton } from '../buttons/CreateButton'
import { FlexCol } from '../flex'

const TEXT_STYLE_MAP = {
  H1: 'lg/text-lg Semibold',
  H2: 'base/text-base Medium',
  H3: 'sm/text-sm Medium',
} as const

interface HeaderProps<T extends Key = Key> {
  variant: keyof typeof TEXT_STYLE_MAP
  title: string
  numOfElements?: number
  titleTags?: ReactNode
  shouldWrapTitleRow?: boolean
  infoTooltip?: string
  titleIconLeft?: React.ReactNode
  primaryButton?: Omit<BaseButtonProps, 'variant' | 'size'> & { tooltip?: Omit<TooltipProps, 'children'> }
  secondaryButton?: Omit<BaseButtonProps, 'variant' | 'size'> & { tooltip?: Omit<TooltipProps, 'children'> }
  secondaryButton2?: Omit<BaseButtonProps, 'variant' | 'size'> & { tooltip?: Omit<TooltipProps, 'children'> }
  customButton?: ReactElement
  tabs?: Omit<TabSwitcherProps<T>, 'colorScheme'>
  datePicker?: DatePickerProps
  extraContent?: React.ReactNode
  extraContentAlign?: 'left' | 'right'
}

const ButtonWithTooltip = ({ button, tooltip = {} }: { button: React.ReactNode; tooltip?: Omit<TooltipProps, 'children'> }) => {
  return (
    <Tooltip {...tooltip} bg='white' color='gray.800' fontWeight='normal' p='4' borderRadius='base' hasArrow placement='top'>
      {button}
    </Tooltip>
  )
}

export const Header = <T extends Key>({
  title,
  variant,
  infoTooltip,
  numOfElements,
  titleTags,
  shouldWrapTitleRow = false,
  titleIconLeft,
  primaryButton,
  secondaryButton,
  secondaryButton2,
  customButton,
  tabs,
  datePicker,
  extraContent,
  extraContentAlign = 'right',
}: HeaderProps<T>) => {
  const textStyle = TEXT_STYLE_MAP[variant]
  const { t } = useTranslation()

  return (
    <FlexCol gap='20px'>
      <Flex justifyContent='space-between' width={'100%'}>
        <Flex alignItems={'center'} gap='10px' flexWrap={shouldWrapTitleRow ? 'wrap' : 'nowrap'} rowGap='8px'>
          {titleIconLeft}
          <Flex alignItems={'center'} gap='4px'>
            <BaseText variant='title' textStyle={textStyle}>
              {t(title)}
            </BaseText>
            {infoTooltip && <BaseTooltip blackIcon label={infoTooltip} width={'16px'} height={'16px'} />}
          </Flex>
          {numOfElements !== undefined && <NumberBox number={numOfElements} />}
          {titleTags}
        </Flex>
        <Flex alignItems={'center'} gap='8px'>
          {customButton}
          {secondaryButton && (
            <ButtonWithTooltip
              button={<BaseButton {...secondaryButton} variant='secondary' size='small' />}
              tooltip={secondaryButton.tooltip}
            />
          )}
          {secondaryButton2 && (
            <ButtonWithTooltip
              button={<BaseButton {...secondaryButton2} variant='secondary' size='small' />}
              tooltip={secondaryButton2.tooltip}
            />
          )}
          {primaryButton && <ButtonWithTooltip button={<CreateButton {...primaryButton} size='small' />} tooltip={primaryButton.tooltip} />}
        </Flex>
      </Flex>
      {(tabs || datePicker || extraContent) && (
        <Flex justifyContent={tabs ? 'space-between' : 'flex-end'} gap='8px' width='100%'>
          {tabs && <TabSwitcher {...tabs} colorScheme='blue' />}
          {extraContentAlign === 'left' && extraContent}
          <Flex alignItems={'center'} gap='8px'>
            {extraContentAlign === 'right' && extraContent}
            {datePicker && <DatePicker {...datePicker} />}
          </Flex>
        </Flex>
      )}
    </FlexCol>
  )
}
